/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UnknownAction, Store } from '@reduxjs/toolkit';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { MemoryRouter, Routes } from 'react-router';
import {
  AuthFetchTrustedServicesProvider,
  useAuthenticationContext,
  useAuthToken,
} from '@opengeoweb/authentication';
import { PluginDebugProvider, PluginProvider } from '@opengeoweb/plugins';
import { ConfirmationServiceProvider } from '@opengeoweb/shared';
import { SnackbarWrapperConnect } from '@opengeoweb/snackbar';
import type { ThemeProviderProps, ThemeTypes } from '@opengeoweb/theme';
import { ThemeWrapper } from '@opengeoweb/theme';
import i18n, { initGeowebI18n } from '../../../i18n';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 0,
      gcTime: 0,
    },
  },
});

const ThemeWrapperWithModules: React.FC<ThemeProviderProps> = ({
  theme,
  children,
  defaultThemeName,
}: ThemeProviderProps) => (
  <ThemeWrapper defaultThemeName={defaultThemeName} theme={theme}>
    {children}
  </ThemeWrapper>
);

const AppPluginDebugProvider: React.FC<{
  isEnabled?: boolean;
  children: React.ReactNode;
}> = ({ isEnabled, children }) =>
  isEnabled ? (
    <PluginDebugProvider>{children}</PluginDebugProvider>
  ) : (
    <>{children}</>
  );

export const AppWrapper: React.FC<{
  children: React.ReactNode;
  store: Store<unknown, UnknownAction>;
  defaultThemeName?: ThemeTypes;
  shouldEnablePluginDebug?: boolean;
}> = ({ children, store, defaultThemeName, shouldEnablePluginDebug }) => {
  const authContext = useAuthenticationContext();
  // Re-renders on token refresh so AuthFetchTrustedServicesProvider stays in sync.
  useAuthToken();

  return (
    <Provider store={store}>
      <ThemeWrapperWithModules defaultThemeName={defaultThemeName}>
        <PluginProvider>
          <AppPluginDebugProvider isEnabled={shouldEnablePluginDebug}>
            <SnackbarWrapperConnect>
              <ConfirmationServiceProvider>
                <AuthFetchTrustedServicesProvider {...authContext}>
                  {children as React.ReactElement}
                </AuthFetchTrustedServicesProvider>
              </ConfirmationServiceProvider>
            </SnackbarWrapperConnect>
          </AppPluginDebugProvider>
        </PluginProvider>
      </ThemeWrapperWithModules>
    </Provider>
  );
};

interface TestWrapperProps {
  store: Store<unknown, UnknownAction>;
  children: React.ReactNode;
}

export const TestWrapper: React.FC<TestWrapperProps> = ({
  store,
  children,
}: TestWrapperProps) => {
  initGeowebI18n();
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <AppWrapper store={store}>{children}</AppWrapper>
      </I18nextProvider>
    </QueryClientProvider>
  );
};

export const TestWrapperWithRouter: React.FC<TestWrapperProps> = ({
  store,
  children,
}: TestWrapperProps) => {
  return (
    <TestWrapper store={store}>
      <MemoryRouter>
        <Routes>{children}</Routes>
      </MemoryRouter>
    </TestWrapper>
  );
};
